import { useCallback, useEffect, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { fetchReplyDraft, requestReplyDraft, type ReplyChannel, type ReplyDraft } from './replyDraftApi'

/**
 * Кнопка «Черновик ответа» рядом с полем ответа (тикет #134): письмо, мессенджер, обращение.
 *
 * Готовый черновик **вставляется в поле**, а не отправляется: дальше его правит менеджер, и
 * уходит то, что он оставил, — обычной кнопкой отправки. Под кнопкой видно, на что черновик
 * опирался: договоры с их условиями и записи истории, а также чего у него не было — договоров,
 * закрытых для вас, и базы знаний, которой в CRM нет.
 */

const WORKING_POLL_MS = 2000
const RETRY_POLL_MS = 30000

export interface ReplyDraftButtonProps {
  channel: ReplyChannel
  sourceId: string
  /** Поле ответа получает текст черновика и его задачу — её отправка передаст серверу. */
  onDraft: (text: string, taskId: string) => void
}

export function ReplyDraftButton({ channel, sourceId, onDraft }: ReplyDraftButtonProps) {
  const { t, user } = useApp()
  const [draft, setDraft] = useState<ReplyDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)
  const inserted = useRef<string | null>(null)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const message = useCallback(
    (failure: unknown) => (failure instanceof ApiError ? failure.message : t('app.error')),
    [t],
  )

  const settle = useCallback(
    (current: ReplyDraft) => {
      setDraft(current)
      if (current.state === 'ready' && current.reply != null && inserted.current !== current.taskId) {
        // Once per draft: a later poll must not overwrite what the manager has already edited.
        inserted.current = current.taskId
        onDraft(current.reply, current.taskId)
      }
    },
    [onDraft],
  )

  const follow = useCallback(
    (current: ReplyDraft) => {
      if (timer.current) clearTimeout(timer.current)
      if (current.state !== 'working' && current.state !== 'retrying') return
      timer.current = setTimeout(() => {
        fetchReplyDraft(current.taskId)
          .then((next) => {
            if (!alive.current) return
            settle(next)
            follow(next)
          })
          .catch((failure: unknown) => alive.current && setError(message(failure)))
      }, current.state === 'working' ? WORKING_POLL_MS : RETRY_POLL_MS)
    },
    [message, settle],
  )

  if (!can(user, 'ai.assistant', 'use')) return null

  const start = () => {
    setBusy(true)
    setError(null)
    requestReplyDraft(channel, sourceId)
      .then((created) => {
        if (!alive.current) return
        settle(created)
        follow(created)
      })
      .catch((failure: unknown) => alive.current && setError(message(failure)))
      .finally(() => alive.current && setBusy(false))
  }

  const working = draft != null && (draft.state === 'working' || draft.state === 'retrying')
  const contracts = (draft?.sources ?? []).filter((source) => source.kind === 'contract')
  const cited = new Set((draft?.cited ?? []).map((source) => source.label))
  const history = (draft?.sources ?? []).filter((source) => source.kind !== 'contract' && cited.has(source.label))

  return (
    <div className="stack" style={{ gap: 6 }} aria-label={t('replyDraft.title')}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy || working} onClick={start}>
          {draft?.state === 'ready' ? t('replyDraft.again') : t('replyDraft.button')}
        </button>
        {working && <span className="t-sm muted" role="status">{draft?.message ?? t('replyDraft.working')}</span>}
        {draft?.state === 'ready' && <span className="t-sm muted" role="status">{t('replyDraft.inserted')}</span>}
      </div>
      {error && <span className="t-sm" role="alert" style={{ color: 'var(--danger-text)' }}>{error}</span>}
      {draft?.state === 'failed' && (
        <span className="t-sm" role="alert" style={{ color: 'var(--danger-text)' }}>
          {t('replyDraft.failed', { reason: draft.message ?? '' })}
        </span>
      )}
      {draft?.state === 'ready' && (
        <div className="stack t-sm" style={{ gap: 4 }} aria-label={t('replyDraft.basis')}>
          <span className="strong">{t('replyDraft.basis')}</span>
          {contracts.length === 0 ? (
            <span className="muted">{t('replyDraft.noContracts')}</span>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }} aria-label={t('replyDraft.contracts')}>
              {contracts.map((contract) => (
                <li key={contract.label}>
                  {contract.title}
                  {cited.has(contract.label) ? ` · ${t('replyDraft.cited')}` : ''}
                  {contract.detail ? <span className="muted"> — {contract.detail}</span> : null}
                </li>
              ))}
            </ul>
          )}
          {history.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18 }} aria-label={t('replyDraft.history')}>
              {history.map((source) => (
                <li key={source.label}>
                  {source.title}
                  {source.at ? <span className="muted"> · {new Date(source.at).toLocaleDateString()}</span> : null}
                </li>
              ))}
            </ul>
          )}
          {draft.contractsWithheld && <span role="note">{t('replyDraft.contractsWithheld')}</span>}
          {(draft.unknownLabels ?? []).length > 0 && (
            <span role="note">{t('replyDraft.unknown', { labels: (draft.unknownLabels ?? []).join(', ') })}</span>
          )}
          {!draft.knowledgeBase && <span className="muted" role="note">{t('replyDraft.noKnowledgeBase')}</span>}
          <span className="muted">{t('replyDraft.review')}</span>
        </div>
      )}
    </div>
  )
}
